import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import api, { API_BASE, fileUrl, fileKey, keyByFile, errMsg } from "../lib/api";
import { toast, NoticeRegion } from "../lib/notices";
import { Loader2, Download, RefreshCw, Trash2, FileText, Video, Share2, Copy, Languages, Plus, X, Image as ImageIcon, Wand2, Send, CheckCircle2 } from "lucide-react";
import { track } from "../lib/analytics";
import ScreenshotAnnotator from "../components/ScreenshotAnnotator";
import ScreenshotReorder from "../components/ScreenshotReorder";
import { formatDateTime } from "../lib/datetime";
import SettingsPanel, { humanize } from "../components/SettingsPanel";
import ClonedVoicePicker from "../components/ClonedVoicePicker";
import { useConfig } from "../lib/config";

const IN_PROGRESS = new Set(["transcribing","scripting","voicing","rendering","manual"]);
const DUB_ACTIVE = new Set(["queued","translating","voicing","rendering"]);

// The status stream sends only each dub's progress fields (lang, status,
// progress, message, error) -- not its video, audio, captions or script.
// Replacing the loaded dubs with that list stripped the finished Telugu dub's
// video while a Kannada dub was rendering, so merge by language instead.
const mergeStreamedDubs = (loaded, streamed) => {
  const byLang = new Map((loaded || []).map((d) => [d.lang, d]));
  return streamed.map((d) => ({ ...(byLang.get(d.lang) || {}), ...d }));
};
const VARIANT_ACTIVE = new Set(["queued","scripting","voicing","rendering"]);
const SOCIAL_PLATFORMS = [
  { id: "tiktok", label: "TikTok" },
  { id: "instagram", label: "Instagram" },
  { id: "youtube", label: "YouTube" },
  { id: "facebook", label: "Facebook" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "x", label: "X" },
];

export default function ProjectDetail() {
  const { featureEnabled } = useConfig();
  const { id } = useParams();
  const nav = useNavigate();
  const [proj, setProj] = useState(null);
  const [tab, setTab] = useState("video");
  const [shareUrl, setShareUrl] = useState("");
  const [showDubModal, setShowDubModal] = useState(false);
  const [dubLang, setDubLang] = useState("hi-IN");
  // Empty = "server default". Hardcoding a speaker id here broke every
  // dub when the TTS model moved to bulbul:v3 and retired `anushka`.
  const [dubVoice, setDubVoice] = useState("");
  // { engines: [{id,name,available,reason,languages,voices}], default } from
  // GET /projects/:id/dub-engines. Drives both the dialog's engine picker and
  // the failed-dub cards' "Retry with Pravaani" button.
  const [dubEngines, setDubEngines] = useState(null);
  const [dubEngine, setDubEngine] = useState("sarvam");

  // Same format YouTube parses: m:ss (h:mm:ss past an hour), first at 0:00.
  const chaptersText = (proj?.chapters || [])
    .map((c) => {
      const t = Math.max(0, Math.floor(c.start_seconds || 0));
      const h = Math.floor(t / 3600), mm = Math.floor(t / 60) % 60, ss = t % 60;
      const stamp = h
        ? `${h}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
        : `${mm}:${String(ss).padStart(2, "0")}`;
      return `${stamp} ${c.title}`;
    })
    .join("\n");
  const [availLangs, setAvailLangs] = useState([]);
  const [stockVoices, setStockVoices] = useState([]);
  const [creatingDub, setCreatingDub] = useState(false);
  const [selectedShotIdx, setSelectedShotIdx] = useState(0);
  const [reordering, setReordering] = useState(false);

  // Indices, not URLs: screenshot links are presigned and regenerated on every
  // read, so they are not stable identifiers.
  const [versionBusy, setVersionBusy] = useState("");

  const restoreVersion = async (v) => {
    if (!window.confirm(
      `Restore version ${v.index}?\n\nThe render currently showing is saved as a new version first, so nothing is lost.`
    )) return;
    setVersionBusy(v.version_id);
    try {
      const r = await api.post(`/projects/${id}/versions/${v.version_id}/restore`);
      setProj(r.data);
      toast.success(`Version ${v.index} restored`);
    } catch (e) { toast.error(errMsg(e, "Could not restore that version")); }
    finally { setVersionBusy(""); }
  };

  const removeVersion = async (v) => {
    if (!window.confirm(`Delete version ${v.index} permanently? Its files are removed.`)) return;
    setVersionBusy(v.version_id);
    try {
      await api.delete(`/projects/${id}/versions/${v.version_id}`);
      setProj((cur) => ({ ...cur, versions: (cur.versions || []).filter(x => x.version_id !== v.version_id) }));
      toast.success(`Version ${v.index} deleted`);
    } catch (e) { toast.error(errMsg(e, "Could not delete that version")); }
    finally { setVersionBusy(""); }
  };

  const addShots = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setReordering(true);
    try {
      const r = await api.post(`/projects/${id}/screenshots`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProj((cur) => ({ ...cur, screenshots: r.data.screenshots }));
      toast.success(`${files.length} screenshot(s) added — regenerate to include them`);
    } catch (e) { toast.error(errMsg(e, "Could not upload those screenshots")); }
    finally { setReordering(false); }
  };

  const deleteShot = async (index) => {
    const previous = proj.screenshots;
    setProj((cur) => ({ ...cur, screenshots: cur.screenshots.filter((_, i) => i !== index) }));
    setSelectedShotIdx((cur) => Math.max(0, cur >= index ? cur - 1 : cur));
    setReordering(true);
    try {
      const r = await api.delete(`/projects/${id}/screenshots/${index}`);
      setProj((cur) => ({ ...cur, screenshots: r.data.screenshots }));
    } catch (e) {
      setProj((cur) => ({ ...cur, screenshots: previous }));
      toast.error(errMsg(e, "Could not remove that screenshot"));
    } finally { setReordering(false); }
  };

  const reorderShots = async (order, movedTo) => {
    const previous = proj.screenshots;
    setProj((cur) => ({ ...cur, screenshots: order.map((i) => cur.screenshots[i]) }));
    setSelectedShotIdx(movedTo);
    setReordering(true);
    try {
      await api.put(`/projects/${id}/screenshots/order`, { order });
    } catch (e) {
      setProj((cur) => ({ ...cur, screenshots: previous }));
      toast.error(errMsg(e, "Could not save the new slide order"));
    } finally {
      setReordering(false);
    }
  };
  const [shotAnnotations, setShotAnnotations] = useState({});
  const [savingAnnotations, setSavingAnnotations] = useState(false);
  // Batch variations (Feature D)
  const [variantCount, setVariantCount] = useState(2);
  const [creatingVariants, setCreatingVariants] = useState(false);
  // Social publishing (Feature E)
  const [showPublish, setShowPublish] = useState(false);
  const [publishPlatforms, setPublishPlatforms] = useState(["tiktok"]);
  const [publishCaption, setPublishCaption] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [socialStatus, setSocialStatus] = useState(null);
  // Output settings edit (aspect ratio + b-roll, Features B & C)
  const [savingOutput, setSavingOutput] = useState(false);
  // Regenerate confirmation (closes M2 -- see askRegenerate/doRegenerate below)
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [quota, setQuota] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/projects/${id}`);
      setProj(r.data);
      // Keyed by fileKey: screenshot URLs are signed, so their query changes.
      setShotAnnotations(keyByFile(r.data.screenshot_annotations));
    }
    catch { nav("/dashboard"); }
  };

  useEffect(()=>{ load(); },[id]);
  useEffect(()=>{
    api.get("/providers/languages").then(r=>setAvailLangs(r.data?.languages || [])).catch(()=>{});
    api.get("/providers/stock-voices", { params: { kind: "sarvam" } }).then(r=>setStockVoices(r.data?.voices || [])).catch(()=>{});
    api.get("/social/status").then(r=>setSocialStatus(r.data)).catch(()=>{});
  }, []);
  // So a failed dub's card knows whether Pravaani is available for "Retry
  // with Pravaani" without waiting for the dialog to be opened.
  useEffect(()=>{
    if (!id) return;
    api.get(`/projects/${id}/dub-engines`).then(r=>setDubEngines(r.data)).catch(()=>{});
  }, [id]);

  const hasActiveDub = (proj?.dubs || []).some(d => DUB_ACTIVE.has(d.status));
  // Languages the status stream has reported as in progress; see below.
  const activeDubLangs = useRef(new Set());
  const hasActiveVariant = (proj?.variants || []).some(v => VARIANT_ACTIVE.has(v.status));
  // Whichever Regenerate/Retry button opened the confirm dialog, so focus can
  // return to it on Cancel/Escape/confirm instead of being lost to <body>.
  const regenerateOpenerRef = useRef(null);
  // The dialog's only two focusable elements, for the Tab/Shift+Tab trap.
  const regenCancelRef = useRef(null);
  const regenConfirmRef = useRef(null);
  useEffect(() => {
    if (!showRegenConfirm) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setShowRegenConfirm(false); };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      regenerateOpenerRef.current?.focus();
    };
  }, [showRegenConfirm]);

  // Variant progress isn't part of the status SSE, so poll while any is running.
  useEffect(() => {
    if (!hasActiveVariant) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [hasActiveVariant, id]);

  useEffect(() => {
    if (!id || !proj) return;
    if (!IN_PROGRESS.has(proj.status) && !hasActiveDub) return;

    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/projects/${id}/stream-status`, {
          credentials: "include",
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          load();
          return;
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += dec.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";
          for (const block of parts) {
            const m = block.match(/^data: (.*)$/s);
            if (!m) continue;
            const chunk = m[1];
            if (chunk === "[DONE]") {
              load();
              return;
            }
            const patch = JSON.parse(chunk);
            if (patch.error) return;
            // A dub that finishes while another is still running keeps this
            // stream open, so its new video would only arrive with the next
            // full load -- fetch it now instead.
            let dubFinished = false;
            for (const d of patch.dubs || []) {
              if (DUB_ACTIVE.has(d.status)) activeDubLangs.current.add(d.lang);
              else if (activeDubLangs.current.delete(d.lang)) dubFinished = true;
            }
            setProj((prev) => {
              if (!prev) return prev;
              const next = { ...prev, ...patch };
              if (patch.dubs) next.dubs = mergeStreamedDubs(prev.dubs, patch.dubs);
              if (patch.video_path) next.video_path = patch.video_path;
              if (patch.manual_pdf_path) next.manual_pdf_path = patch.manual_pdf_path;
              return next;
            });
            if (dubFinished) load();
          }
        }
      } catch (err) {
        if (err?.name !== "AbortError") load();
      }
    })();
    return () => ctrl.abort();
  }, [id, proj?.status, hasActiveDub]);

  useEffect(()=>{
    if (proj?.status === "ready") setTab(proj.video_path ? "video" : "manual");
  }, [proj?.status]);

  // Deep-link from the Launch Kit "Publish video" CTA (?publish=1) opens the modal.
  useEffect(()=>{
    if (featureEnabled("social_publish") && proj?.status === "ready" && proj?.video_path &&
        new URLSearchParams(window.location.search).get("publish") === "1") {
      setPublishCaption(proj.description || proj.title || "");
      setShowPublish(true);
    }
  }, [proj?.status, proj?.video_path, featureEnabled]);

  if (!proj) return <div className="min-h-screen"><Header/><main id="main" tabIndex={-1} className="text-center py-20">Loading…</main></div>;

  // Single source of truth for "the page treats this caller as the project
  // owner" -- `my_role` absent means owner. Used both to decide whether to
  // fetch /billing/me (only the owner's quota is meaningful, see
  // askRegenerate) and to pick which quota line the dialog renders.
  const role = proj.my_role || "owner";
  const isOwnerRole = role === "owner";

  const doRegenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    setShowRegenConfirm(false);
    try {
      if (Object.keys(shotAnnotations).length > 0) {
        await api.put(`/projects/${id}/annotations`, { annotations: shotAnnotations });
      }
      await api.post(`/projects/${id}/generate`);
      toast.success("Regenerating…");
      load();
    }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRegenerating(false); }
  };

  const askRegenerate = () => {
    setQuota(null);
    if (isOwnerRole) {
      // Quota is informational: if the fetch fails the dialog simply omits it.
      api.get("/billing/me").then((r) => setQuota(r.data)).catch(() => setQuota(null));
    }
    setShowRegenConfirm(true);
  };
  // Shared onClick for every button that opens the confirm dialog (Regenerate,
  // Retry, Retry with these settings) so Escape/Cancel can return focus to it.
  const openRegen = (e) => { regenerateOpenerRef.current = e.currentTarget; askRegenerate(); };
  // Tab/Shift+Tab trap: the dialog's only two focusable elements are Cancel
  // and Regenerate, so keep focus cycling between them instead of escaping
  // to the page behind the overlay.
  const trapRegenTab = (e) => {
    if (e.key !== "Tab") return;
    const first = regenCancelRef.current;
    const last = regenConfirmRef.current;
    if (!first || !last) return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const saveAnnotations = async () => {
    setSavingAnnotations(true);
    try {
      const r = await api.put(`/projects/${id}/annotations`, { annotations: shotAnnotations });
      setShotAnnotations(keyByFile(r.data.screenshot_annotations));
      toast.success("Annotations saved");
    } catch (e) {
      toast.error(errMsg(e, "Failed to save annotations"));
    } finally {
      setSavingAnnotations(false);
    }
  };
  const del = async () => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`); nav("/dashboard");
  };

  // ---- Batch variations (Feature D) ----
  const createVariations = async () => {
    setCreatingVariants(true);
    try {
      await api.post(`/projects/${id}/variations`, { count: variantCount });
      toast.success(`Generating ${variantCount} variation${variantCount>1?"s":""}…`);
      load();
    } catch(e){ toast.error(errMsg(e, "Batch variations require a Pro plan")); }
    finally { setCreatingVariants(false); }
  };
  const selectVariant = async (vid) => {
    try {
      await api.post(`/projects/${id}/variations/${vid}/select`);
      toast.success("Variation selected — it's now your main video");
      load();
    } catch(e){ toast.error(errMsg(e, "Failed")); }
  };
  const deleteVariant = async (vid) => {
    try { await api.delete(`/projects/${id}/variations/${vid}`); load(); }
    catch(e){ toast.error(errMsg(e, "Failed")); }
  };

  // ---- Output settings (aspect ratio + b-roll, Features B & C) ----
  const saveOutput = async (patch) => {
    setSavingOutput(true);
    try { const r = await api.patch(`/projects/${id}`, patch); setProj(r.data); }
    catch(e){ toast.error(errMsg(e, "Failed")); }
    finally { setSavingOutput(false); }
  };

  // ---- Social publishing (Feature E) ----
  const connectSocial = async () => {
    try {
      const r = await api.post("/social/connect");
      setSocialStatus(s => ({ ...(s||{}), profile: r.data.profile, connect_url: r.data.connect_url }));
      if (r.data.connect_url) window.open(r.data.connect_url, "_blank");
    } catch(e){ toast.error(errMsg(e, "Connect failed")); }
  };
  const publishNow = async () => {
    if (!publishPlatforms.length) { toast.error("Pick at least one platform"); return; }
    setPublishing(true);
    try {
      await api.post(`/projects/${id}/publish`, { platforms: publishPlatforms, caption: publishCaption });
      toast.success("Publishing queued");
      setShowPublish(false);
      load();
    } catch(e){ toast.error(errMsg(e, "Publishing requires a Creator+ plan")); }
    finally { setPublishing(false); }
  };
  const togglePlatform = (pid) => {
    setPublishPlatforms(ps => ps.includes(pid) ? ps.filter(x=>x!==pid) : [...ps, pid]);
  };

  const inProgress = IN_PROGRESS.has(proj.status);
  const canWrite = role === "owner" || role === "editor";
  const canDelete = role === "owner";

  // Same two-switch rule as Settings.jsx and the wizard: Pravaani ships
  // independently of the generic voice_cloning flag.
  const cloningAvailable = featureEnabled("voice_cloning") || featureEnabled("voice_cloning_pravaani");
  // Which cloned voice narrates this project is a project setting, so it sits
  // with the rest of them — at both places SettingsPanel is offered (a failed
  // render and a finished one), since a wrong voice is a reason for either.
  // Not folded into SettingsPanel itself because PATCH /projects deliberately
  // refuses `cloned_voice_id`; this goes through its own ownership-checked
  // route and then reloads the project so the repointed TTS provider shows.
  const clonedVoicePicker = cloningAvailable ? (
    <ClonedVoicePicker
      projectId={id}
      value={proj.cloned_voice_id ? {
        cloned_voice_id: proj.cloned_voice_id,
        cloned_voice_provider: proj.cloned_voice_provider,
        cloned_voice_name: proj.cloned_voice_name,
      } : null}
      onChange={() => load()}
    />
  ) : null;

  // The live render's permanent number. Projects rendered before this field
  // existed have no live_version — fall back to "one past the highest
  // archived version" so the current video still gets a plausible number.
  const liveVersionNumber = proj.live_version ?? (
    proj.video_path
      ? 1 + Math.max(0, ...(proj.versions || []).map((v) => v.index || 0))
      : null
  );

  // Dub dialog: which engine's language/voice lists to show. Falls back to the
  // pre-existing global lists (and effectively "sarvam" on create) whenever
  // /dub-engines hasn't loaded, failed, or the selected engine reports none —
  // an empty select with no error was the alternative, which is unusable.
  const availableDubEngines = (dubEngines?.engines || []).filter(e => e.available);
  const selectedDubEngine = availableDubEngines.find(e => e.id === dubEngine) || availableDubEngines[0];
  const dubEngineLangsFallback = !(selectedDubEngine?.languages || []).length;
  const dubEngineVoicesFallback = !(selectedDubEngine?.voices || []).length;
  const dubDialogLangs = dubEngineLangsFallback ? (availLangs || []) : selectedDubEngine.languages;
  const dubDialogVoices = (dubEngineVoicesFallback ? (stockVoices || []) : selectedDubEngine.voices)
    .filter(v => v.id !== "");
  // The provider actually sent on create: falls back to "sarvam" (today's
  // behaviour) whenever the dialog itself fell back to the global lists.
  const dubCreateProvider = dubEngineLangsFallback ? "sarvam" : (selectedDubEngine?.id || "sarvam");

  // Regenerate dialog's quota line. `used` with no sane meaning (missing,
  // NaN) means the whole line is hidden rather than showing something
  // confusing; a missing/negative `limit` is this app's existing "unlimited"
  // convention, not zero renders left.
  const quotaUsedValid = !!quota && Number.isFinite(quota.used);
  const quotaUnlimited = quotaUsedValid && !(Number.isFinite(quota.limit) && quota.limit >= 0);
  const quotaRemaining = quotaUsedValid && !quotaUnlimited ? Math.max(0, quota.limit - quota.used) : null;

  return (
    <div className="min-h-screen">
      <Header />
      <main id="main" tabIndex={-1} className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <NoticeRegion />
        <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
          <div>
            <div className="marquee-tag mb-2">// PROJECT · {proj.status.toUpperCase()}</div>
            <h1 className="font-display font-black type-title tracking-tighter" data-testid="project-title">{proj.title}</h1>
            <p className="text-ink-muted mt-2 max-w-2xl">{proj.description}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {proj.status === "ready" && (
              <>
                {canWrite && (
                  <button onClick={async()=>{
                    try {
                      const r = await api.patch(`/projects/${id}`, { hide_branding: !proj.hide_branding });
                      setProj(r.data);
                      toast.success(r.data.hide_branding ? "Branding hidden" : "Branding shown");
                    } catch(e){ toast.error(e?.response?.data?.detail || "Failed — Starter/Pro required"); }
                  }} className="brutal-btn brutal-btn-secondary" data-testid="btn-toggle-branding">
                    {proj.hide_branding ? "Show" : "Hide"} branding
                  </button>
                )}
                {canWrite && (
                  <button onClick={async()=>{
                    const r = await api.post(`/projects/${id}/share`);
                    const url = `${window.location.origin}/share/${r.data.share_token}`;
                    setShareUrl(url);
                    try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard"); }
                    catch { toast.success("Share link created"); }
                  }} className="brutal-btn brutal-btn-secondary" data-testid="btn-share"><Share2 size={14}/> Share</button>
                )}
                {canWrite && (
                  <Link to={`/project/${id}/storyboard`} className="brutal-btn brutal-btn-secondary" data-testid="btn-storyboard">Storyboard</Link>
                )}
                {canWrite && (
                  <Link to={`/project/${id}/launch-kit`} className="brutal-btn brutal-btn-secondary" data-testid="btn-launch-kit">Launch Kit</Link>
                )}
                {canWrite && proj.video_path && featureEnabled("social_publish") && (
                  <button
                    onClick={()=>{ setPublishCaption(proj.description || proj.title || ""); setShowPublish(true); }}
                    className="brutal-btn brutal-btn-secondary"
                    data-testid="btn-publish"
                  ><Send size={14}/> Publish</button>
                )}
                <button
                  type="button"
                  className="brutal-btn brutal-btn-secondary"
                  data-testid="btn-export"
                  onClick={async () => {
                    track("export_clicked", { project_id: id, format: "zip" });
                    window.open(`${API_BASE}/projects/${id}/export/package`, "_blank");
                  }}
                >
                  <Download size={14}/> Export
                </button>
                {canWrite && (
                  <button onClick={openRegen} className="brutal-btn brutal-btn-secondary" data-testid="btn-regenerate"><RefreshCw size={14}/> Regenerate</button>
                )}
                {canWrite && (
                  <label className="field-focusable brutal-btn brutal-btn-secondary cursor-pointer inline-flex items-center gap-1" data-testid="btn-upload-brand-logo">
                    <ImageIcon size={14}/> {proj.brand_logo ? "Change logo" : "Upload logo"}
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData();
                        fd.append("file", f);
                        try {
                          const r = await api.post(`/projects/${id}/brand-kit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                          setProj((p) => ({ ...p, brand_logo: r.data.brand_logo }));
                          toast.success("Logo uploaded — click Regenerate to add intro/outro cards");
                        } catch (err) {
                          toast.error(errMsg(err) || "Upload failed");
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </>
            )}
            {proj.status === "failed" && canWrite && (
              <button onClick={openRegen} className="brutal-btn" data-testid="btn-retry"><RefreshCw size={14}/> Retry</button>
            )}
            {canDelete && (
              <button onClick={del} className="brutal-btn brutal-btn-secondary" data-testid="btn-delete" aria-label="Delete this project"><Trash2 size={14}/></button>
            )}
            {!canWrite && (
              <span className="brutal-chip" data-testid="role-viewer-badge">VIEWER · READ-ONLY</span>
            )}
          </div>
        </div>
        {shareUrl && (
          <div className="brutal-card mb-6 flex items-center gap-3" data-testid="share-url-panel">
            <div className="brutal-chip">SHARE LINK</div>
            <input readOnly value={shareUrl} className="brutal-input flex-1" data-testid="share-url-input"/>
            <button onClick={()=>{navigator.clipboard.writeText(shareUrl); toast.success("Copied");}} className="brutal-btn brutal-btn-secondary" aria-label="Copy share link"><Copy size={14}/></button>
          </div>
        )}

        {inProgress && (
          <div className="brutal-card mb-8" data-testid="progress-panel">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="animate-spin"/>
              <div className="font-display font-bold type-subheading">{proj.status_message || "Processing..."}</div>
            </div>
            <div className="progress-bar"><div className="progress-fill" style={{width:`${proj.progress}%`}}/></div>
            <div className="text-xs uppercase tracking-widest text-ink-muted mt-2">{proj.progress}% COMPLETE</div>
          </div>
        )}

        {proj.status === "failed" && (
          <div className="brutal-card mb-8 bg-state-danger-surface" data-testid="error-panel">
            <div className="marquee-tag mb-2 bg-brand text-white">// ERROR</div>
            <p className="font-mono-ui text-sm">{proj.error}</p>
            {canWrite && (
              <p className="text-xs text-ink-muted mt-3" data-testid="error-panel-hint">
                Change any setting below, then press Retry.
              </p>
            )}
          </div>
        )}

        {/* A failed render is most often fixed by a setting (a language the
            voice engine doesn't serve, a provider that is out of credit), so
            the same settings a finished video offers are offered here. */}
        {proj.status === "failed" && canWrite && (
          <div className="mb-8" data-testid="failed-settings">
            <SettingsPanel proj={proj} saving={savingOutput} onChange={saveOutput} />
            {clonedVoicePicker}
            <div className="flex justify-end mt-4">
              <button onClick={openRegen} disabled={savingOutput} className="brutal-btn" data-testid="btn-retry-with-settings">
                <RefreshCw size={14}/> Retry with these settings
              </button>
            </div>
          </div>
        )}

        {proj.render_warnings?.length > 0 && (
          <div className="brutal-card mb-8 bg-state-warning-surface" data-testid="render-warnings-panel">
            <div className="marquee-tag mb-2 bg-[#FF9500] text-white">// CHECK YOUR VIDEO</div>
            <ul className="font-mono-ui text-sm list-disc pl-5">
              {proj.render_warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {proj.status === "ready" && (
          <>
            <div className="flex gap-0 mb-6 brutal-border" style={{borderBottomWidth:0}}>
              {proj.video_path && (
                <button onClick={()=>setTab("video")} className={`px-6 py-3 border-r brutal-border border-t-0 border-b-0 border-l-0 font-mono-ui text-xs uppercase tracking-widest ${tab==='video'?'bg-surface-inverse text-white':'bg-white'}`} data-testid="tab-video">
                  <Video size={14} className="inline mr-1"/> Video
                </button>
              )}
              {proj.manual_md && (
                <button onClick={()=>setTab("manual")} className={`px-6 py-3 border-r brutal-border border-t-0 border-b-0 border-l-0 font-mono-ui text-xs uppercase tracking-widest ${tab==='manual'?'bg-surface-inverse text-white':'bg-white'}`} data-testid="tab-manual">
                  <FileText size={14} className="inline mr-1"/> Manual
                </button>
              )}
              <button onClick={()=>setTab("script")} className={`px-6 py-3 font-mono-ui text-xs uppercase tracking-widest border-r brutal-border border-t-0 border-b-0 border-l-0 ${tab==='script'?'bg-surface-inverse text-white':'bg-white'}`} data-testid="tab-script">
                Script
              </button>
              <button onClick={()=>setTab("languages")} className={`px-6 py-3 font-mono-ui text-xs uppercase tracking-widest border-r brutal-border border-t-0 border-b-0 border-l-0 ${tab==='languages'?'bg-surface-inverse text-white':'bg-white'}`} data-testid="tab-languages">
                <Languages size={14} className="inline mr-1"/> Languages
                {(proj.dubs?.length || 0) > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center bg-brand text-white px-1.5 py-0.5 text-2xs font-mono-ui rounded-none">
                    {proj.dubs.length}
                  </span>
                )}
              </button>
              {canWrite && (
                <button onClick={()=>setTab("variations")} className={`px-6 py-3 font-mono-ui text-xs uppercase tracking-widest ${tab==='variations'?'bg-surface-inverse text-white':'bg-white'}`} data-testid="tab-variations">
                  <Wand2 size={14} className="inline mr-1"/> Variations
                  {(proj.variants?.length || 0) > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center bg-brand text-white px-1.5 py-0.5 text-2xs font-mono-ui rounded-none">
                      {proj.variants.length}
                    </span>
                  )}
                </button>
              )}
            </div>

            {tab==="video" && proj.video_path && (
              <div className="brutal-card p-0" data-testid="video-panel">
                {proj.source_type === "screen_recording" && (
                  <div className="p-4 border-b brutal-border bg-surface text-xs text-ink-muted uppercase tracking-widest leading-relaxed" data-testid="screen-rec-hint">
                    Screen recordings are transcoded to MP4 with optional captions. Click/keyboard highlight overlays require the DemoCraft browser extension during capture — without captured events, the video matches your raw recording. Upload a logo and Regenerate to add intro/outro title cards.
                  </div>
                )}
                <video
                  src={fileUrl(proj.video_path)}
                  poster={proj.thumbnail_path ? fileUrl(proj.thumbnail_path) : undefined}
                  controls
                  crossOrigin="anonymous"
                  className="w-full brutal-border border-0"
                  data-testid="video-player"
                >
                  {/* Only attach a text track when the captions are NOT already
                      burnt into the frames. With caption_style="burn" ffmpeg has
                      rendered them through libass, so adding a <track> made the
                      browser draw the same line a second time, offset and in a
                      different font, on top of the baked-in one. The two are
                      alternatives -- see the caption_style note in models.py. */}
                  {proj.captions_vtt_path && proj.caption_style !== "burn" && (
                    <track
                      kind="subtitles"
                      label={proj.language ? `Captions (${proj.language})` : "Captions"}
                      srcLang={(proj.language || "en").split("-")[0]}
                      src={fileUrl(proj.captions_vtt_path)}
                      default
                      data-testid="video-track"
                    />
                  )}
                </video>
                {liveVersionNumber != null && (
                  <div className="px-4 pt-3 font-mono-ui text-2xs text-ink-muted uppercase tracking-widest" data-testid="live-version-label">
                    Version {liveVersionNumber} · current
                  </div>
                )}
                <div className="p-4 flex flex-wrap justify-between items-center gap-2 text-xs uppercase tracking-widest">
                  <span>demo.mp4</span>
                  <div className="flex flex-wrap gap-2">
                    {proj.captions_srt_path && (
                      <a href={fileUrl(proj.captions_srt_path)} download="captions.srt" className="brutal-btn brutal-btn-secondary" data-testid="download-srt">
                        <Download size={14}/> .srt
                      </a>
                    )}
                    {proj.captions_vtt_path && (
                      <a href={fileUrl(proj.captions_vtt_path)} download="captions.vtt" className="brutal-btn brutal-btn-secondary" data-testid="download-vtt">
                        <Download size={14}/> .vtt
                      </a>
                    )}
                    <a href={fileUrl(proj.video_path)} download className="brutal-btn" data-testid="download-video"><Download size={14}/> Download</a>
                  </div>
                </div>
                {proj.bg_music_key && (
                  <div className="px-4 pb-4 text-xs uppercase tracking-widest text-ink-muted italic" data-testid="music-attribution">
                    Music credit: <span className="not-italic">{proj.bg_music_key.replace(/_/g," ")}</span> — Kevin MacLeod (incompetech.com) · CC BY 4.0
                  </div>
                )}
              </div>
            )}
            {tab==="video" && proj.video_path && canWrite && (
              <>
                <SettingsPanel proj={proj} saving={savingOutput} onChange={saveOutput} />
                {clonedVoicePicker}
              </>
            )}
            {tab==="manual" && proj.manual_md && (
              <div className="brutal-card" data-testid="manual-panel">
                <div className="flex justify-end mb-4">
                  {proj.manual_pdf_path && (
                    <a href={fileUrl(proj.manual_pdf_path)} download className="brutal-btn" data-testid="download-pdf"><Download size={14}/> Download PDF</a>
                  )}
                </div>
                <article className="prose max-w-none font-mono-ui">
                  <pre className="text-sm" style={{whiteSpace:"pre-wrap", fontFamily:"'IBM Plex Mono', monospace", lineHeight:"1.7"}}>{proj.manual_md}</pre>
                </article>
              </div>
            )}
            {tab==="script" && proj.chapters?.length > 0 && (
              <div className="brutal-card mb-6" data-testid="chapters-panel">
                <h2 className="marquee-tag mb-3">// CHAPTERS</h2>
                <p className="text-xs text-ink-muted mb-3">
                  Paste into a YouTube description to get a clickable chapter list.
                </p>
                <pre className="font-mono-ui text-sm bg-surface-sunken p-3 brutal-border overflow-x-auto"
                     data-testid="chapters-text">{chaptersText}</pre>
                <button
                  type="button"
                  className="brutal-btn brutal-btn-secondary mt-3"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(chaptersText);
                      toast.success("Chapters copied");
                    } catch { toast.error("Could not copy — select the text instead"); }
                  }}
                  data-testid="copy-chapters"
                >
                  <Copy size={14} aria-hidden="true" /> Copy chapters
                </button>
              </div>
            )}
            {tab==="script" && (
              <div className="brutal-card" data-testid="script-panel">
                <div className="marquee-tag mb-3">// VOICE-OVER SCRIPT</div>
                <pre className="text-sm" style={{whiteSpace:"pre-wrap", fontFamily:"'IBM Plex Mono', monospace", lineHeight:"1.7"}}>{proj.script}</pre>
                {proj.voiceover_path && (
                  <div className="mt-4">
                    <audio src={fileUrl(proj.voiceover_path)} controls className="w-full"/>
                    <a href={fileUrl(proj.voiceover_path)} download className="brutal-btn brutal-btn-secondary mt-3 inline-flex" data-testid="download-audio"><Download size={14}/> Download MP3</a>
                  </div>
                )}
              </div>
            )}
            {tab==="languages" && (
              <div className="brutal-card" data-testid="languages-panel">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="marquee-tag mb-1">// MULTI-LANGUAGE DUBBING</div>
                    <div className="text-xs text-ink-muted uppercase tracking-widest">
                      Reuse the source screenshots. Translate the script. Regenerate voiceover.
                    </div>
                  </div>
                  {canWrite && (
                    <button
                      onClick={async ()=>{
                        let eng = dubEngines;
                        try {
                          const r = await api.get(`/projects/${id}/dub-engines`);
                          eng = r.data;
                          setDubEngines(r.data);
                        } catch { /* fall back to whatever we already have, if anything */ }
                        const engineId = eng?.default || "sarvam";
                        const engineObj = (eng?.engines || []).find(x => x.id === engineId);
                        const langs = engineObj?.languages || availLangs || [];
                        // Preselect a lang the project doesn't already have.
                        const existing = new Set((proj.dubs || []).map(d=>d.lang));
                        const candidate = langs.find(l => !existing.has(l.code) && l.code !== proj.language)?.code
                          || langs[0]?.code || "hi-IN";
                        setDubEngine(engineId);
                        setDubLang(candidate);
                        setDubVoice("");
                        setShowDubModal(true);
                      }}
                      className="brutal-btn"
                      data-testid="btn-add-language"
                    >
                      <Plus size={14}/> Add language
                    </button>
                  )}
                </div>
                {(!proj.dubs || proj.dubs.length === 0) ? (
                  <div className="brutal-border p-6 text-center bg-surface" data-testid="dubs-empty">
                    <div className="text-4xl mb-2">🌐</div>
                    <div className="font-display font-black type-subheading tracking-tight">No dubs yet</div>
                    <div className="text-xs text-ink-muted uppercase tracking-widest mt-1">
                      Click "Add language" to translate the script and generate a voiceover in another language.
                    </div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3">
                    {proj.dubs.map(d => {
                      const label = availLangs.find(l => l.code === d.lang)?.label || d.lang;
                      const active = DUB_ACTIVE.has(d.status);
                      const done = d.status === "done";
                      const failed = d.status === "error";
                      return (
                        <div key={d.lang} className="brutal-border p-4 space-y-2" data-testid={`dub-card-${d.lang}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="font-display font-black type-subheading tracking-tight">{label}</div>
                              <div className="text-2xs uppercase tracking-widest text-ink-muted">
                                {d.voice} · {d.provider}
                              </div>
                            </div>
                            <span className={`brutal-chip ${done ? "" : failed ? "bg-brand text-white" : ""}`}>
                              {d.status.toUpperCase()}
                            </span>
                          </div>
                          {active && (
                            <>
                              <div className="text-xs uppercase tracking-widest text-ink-muted">
                                {d.status_message || "Processing…"}
                              </div>
                              <div className="progress-bar"><div className="progress-fill" style={{width:`${d.progress || 0}%`}}/></div>
                            </>
                          )}
                          {failed && d.error && (
                            <div className="text-xs text-ink-accent font-mono-ui" data-testid={`dub-error-${d.lang}`}>
                              {d.error}
                            </div>
                          )}
                          {done && d.video_path && (
                            <video
                              src={fileUrl(d.video_path)}
                              controls
                              crossOrigin="anonymous"
                              className="w-full brutal-border"
                              data-testid={`dub-video-${d.lang}`}
                            >
                              {/* Dubs burn captions too (dubs.py) — see the note
                                  on the main player above. */}
                              {d.captions_vtt_path && proj.caption_style !== "burn" && (
                                <track kind="subtitles" srcLang={d.lang.split("-")[0]} src={fileUrl(d.captions_vtt_path)} default data-testid={`dub-track-${d.lang}`}/>
                              )}
                            </video>
                          )}
                          {done && !d.video_path && d.voiceover_path && (
                            <audio src={fileUrl(d.voiceover_path)} controls className="w-full"/>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {done && d.video_path && (
                              <a href={fileUrl(d.video_path)} download={`demo-${d.lang}.mp4`} className="brutal-btn brutal-btn-secondary" data-testid={`dub-download-video-${d.lang}`}>
                                <Download size={12}/> Video
                              </a>
                            )}
                            {done && d.voiceover_path && (
                              <a href={fileUrl(d.voiceover_path)} download={`voice-${d.lang}.mp3`} className="brutal-btn brutal-btn-secondary" data-testid={`dub-download-audio-${d.lang}`}>
                                <Download size={12}/> Audio
                              </a>
                            )}
                            {done && d.captions_srt_path && (
                              <a href={fileUrl(d.captions_srt_path)} download={`captions-${d.lang}.srt`} className="brutal-btn brutal-btn-secondary" data-testid={`dub-download-srt-${d.lang}`}>
                                <Download size={12}/> SRT
                              </a>
                            )}
                            {canWrite && failed && d.provider !== "pravaani" &&
                              (dubEngines?.engines || []).find(e => e.id === "pravaani")?.available && (
                              <button
                                onClick={async()=>{
                                  try {
                                    // A single call so a failure never leaves the project
                                    // with no dub at all (the server replaces the errored
                                    // dub atomically when replace_failed is set).
                                    await api.post(`/projects/${id}/dubs`, { lang: d.lang, voice: "", provider: "pravaani", replace_failed: true });
                                    toast.success("Retrying with Pravaani");
                                    load();
                                  } catch(e) { toast.error(errMsg(e, "Failed to retry with Pravaani")); }
                                }}
                                className="brutal-btn brutal-btn-secondary"
                                data-testid={`dub-retry-pravaani-${d.lang}`}
                              >
                                <RefreshCw size={12}/> Retry with Pravaani
                              </button>
                            )}
                            {canWrite && (
                              <button
                                onClick={async()=>{
                                  if (!window.confirm(`Delete the ${label} dub?`)) return;
                                  try {
                                    await api.delete(`/projects/${id}/dubs/${d.lang}`);
                                    toast.success("Dub deleted");
                                    load();
                                  } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
                                }}
                                className="brutal-btn brutal-btn-secondary"
                                data-testid={`dub-delete-${d.lang}`}
                                aria-label={`Delete ${d.lang} dub`}
                              >
                                <Trash2 size={12}/>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {tab==="variations" && canWrite && (
              <div className="brutal-card" data-testid="variations-panel">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="marquee-tag mb-1">// BATCH VARIATIONS · PRO</div>
                    <div className="text-xs text-ink-muted uppercase tracking-widest">
                      Generate alternate script/voice takes, then pick the best. Each variation uses one render.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="brutal-label !mb-0">How many</label>
                    <select value={variantCount} onChange={(e)=>setVariantCount(parseInt(e.target.value))} className="brutal-select !w-auto" data-testid="variant-count-select">
                      {[1,2,3].map(n=><option key={n} value={n}>{n}</option>)}
                    </select>
                    <button onClick={createVariations} disabled={creatingVariants || hasActiveVariant} className="brutal-btn" data-testid="btn-create-variations">
                      {creatingVariants ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>} Generate
                    </button>
                  </div>
                </div>
                {(!proj.variants || proj.variants.length === 0) ? (
                  <div className="brutal-border p-6 text-center bg-surface" data-testid="variants-empty">
                    <div className="text-4xl mb-2">🎬</div>
                    <div className="font-display font-black type-subheading tracking-tight">No variations yet</div>
                    <div className="text-xs text-ink-muted uppercase tracking-widest mt-1">
                      Generate a few takes and A/B them side by side.
                    </div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {proj.variants.map(v => {
                      const active = VARIANT_ACTIVE.has(v.status);
                      const done = v.status === "done";
                      const failed = v.status === "error";
                      return (
                        <div key={v.variant_id} className={`brutal-border p-3 space-y-2 ${v.selected ? "ring-2 ring-[#FF3B30]" : ""}`} data-testid={`variant-card-${v.index}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-display font-black type-subheading tracking-tight">{v.label || `Variation ${v.index+1}`}</div>
                            <span className={`brutal-chip ${done ? "" : failed ? "bg-brand text-white" : ""}`}>{(v.selected ? "SELECTED" : v.status).toUpperCase()}</span>
                          </div>
                          <div className="text-2xs uppercase tracking-widest text-ink-muted">voice · {v.voice}</div>
                          {active && (
                            <div className="progress-bar"><div className="progress-fill" style={{width:`${v.progress||0}%`}}/></div>
                          )}
                          {failed && v.error && (
                            <div className="text-xs text-ink-accent font-mono-ui" data-testid={`variant-error-${v.index}`}>{v.error}</div>
                          )}
                          {done && v.video_path && (
                            <video src={fileUrl(v.video_path)} controls crossOrigin="anonymous" className="w-full brutal-border" data-testid={`variant-video-${v.index}`}/>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {done && !v.selected && (
                              <button onClick={()=>selectVariant(v.variant_id)} className="brutal-btn" data-testid={`variant-select-${v.index}`}>
                                <CheckCircle2 size={12}/> Use this
                              </button>
                            )}
                            <button onClick={()=>deleteVariant(v.variant_id)} className="brutal-btn brutal-btn-secondary" data-testid={`variant-delete-${v.index}`} aria-label={`Delete variant ${v.index}`}>
                              <Trash2 size={12}/>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {(proj.versions?.length > 0 || proj.screenshots?.length > 0) && (
          <div className="mt-10">
            {proj.versions?.length > 0 && (
              <div className="mb-8" data-testid="versions-panel">
                <h2 className="marquee-tag mb-3">// PREVIOUS VERSIONS</h2>
                <p className="text-xs text-ink-muted mb-3">
                  Every regeneration keeps the render it replaced. Restoring one saves the
                  current render first, so nothing is lost either way.
                </p>
                <ul className="space-y-2 list-none p-0 m-0">
                  {[...proj.versions].reverse().map((v) => {
                    const changed = Object.entries(v.render_settings || {})
                      .filter(([k, val]) => k !== "rendered_at" && proj[k] !== undefined && proj[k] !== val);
                    return (
                      <li key={v.version_id} className="brutal-border p-3" data-testid={`version-${v.index}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <span className="font-display font-bold">Version {v.index}</span>
                            <span className="ml-3 font-mono-ui text-xs text-ink-muted">
                              {formatDateTime(v.created_at)}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            {v.video_path && (
                              <a href={fileUrl(v.video_path)} download={`demo-v${v.index}.mp4`}
                                 className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                                 data-testid={`version-download-${v.index}`}>
                                <Download size={12} aria-hidden="true" /> MP4
                              </a>
                            )}
                            <button type="button" onClick={() => restoreVersion(v)}
                                    disabled={versionBusy !== "" || inProgress}
                                    className="brutal-btn brutal-btn-sm"
                                    data-testid={`version-restore-${v.index}`}>
                              <RefreshCw size={12} aria-hidden="true" /> Restore
                            </button>
                            <button type="button" onClick={() => removeVersion(v)}
                                    disabled={versionBusy !== ""}
                                    className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                                    aria-label={`Delete version ${v.index}`}
                                    data-testid={`version-delete-${v.index}`}>
                              <Trash2 size={12} aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                        {/* What actually differs from the render showing now — "v1, v2"
                            on its own does not help anyone choose. */}
                        {changed.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {changed.map(([k, val]) => (
                              <span key={k} className="brutal-chip text-2xs">
                                {k.replace(/_/g, " ")}: {String(val)}
                              </span>
                            ))}
                          </div>
                        )}
                        {v.video_path && (
                          <video src={fileUrl(v.video_path)} controls preload="none"
                                 poster={v.thumbnail_path ? fileUrl(v.thumbnail_path) : undefined}
                                 crossOrigin="anonymous"
                                 className="w-full brutal-border mt-3 max-w-md"
                                 data-testid={`version-video-${v.index}`} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {proj.screenshots?.length > 0 && (
              <>
                <div className="marquee-tag mb-3">// SOURCE SCREENSHOTS</div>
                <div className="mb-4">
                  <ScreenshotReorder
                    screenshots={proj.screenshots.map(fileUrl)}
                    selectedIdx={selectedShotIdx}
                    onSelect={setSelectedShotIdx}
                    disabled={reordering || inProgress}
                    onReorder={reorderShots}
                    onDelete={canWrite ? deleteShot : undefined}
                    onAdd={canWrite ? addShots : undefined}
                  />
                </div>
                {proj.screenshots[selectedShotIdx] && (
                  <ScreenshotAnnotator
                    screenshotUrl={fileUrl(proj.screenshots[selectedShotIdx])}
                    annotations={shotAnnotations[fileKey(proj.screenshots[selectedShotIdx])] || []}
                    onChange={(next) => {
                      const url = fileKey(proj.screenshots[selectedShotIdx]);
                      setShotAnnotations((prev) => {
                        const copy = { ...prev };
                        if (next.length) copy[url] = next;
                        else delete copy[url];
                        return copy;
                      });
                    }}
                    onSave={canWrite && !inProgress ? saveAnnotations : undefined}
                    saving={savingAnnotations}
                    readOnly={!canWrite || inProgress}
                  />
                )}
              </>
            )}
          </div>
        )}
      </main>

      {showDubModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="add-language-modal" onClick={(e)=>{ if(e.target===e.currentTarget) setShowDubModal(false); }}>
          <div className="brutal-card bg-white w-full max-w-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="marquee-tag mb-1">// ADD A LANGUAGE DUB</div>
                <div className="text-xs uppercase tracking-widest text-ink-muted">
                  Translate the script + regenerate voiceover. Video is re-rendered with the same screenshots.
                </div>
              </div>
              <button onClick={()=>setShowDubModal(false)} className="brutal-btn brutal-btn-ghost" data-testid="btn-close-dub-modal" aria-label="Close dialog"><X size={14}/></button>
            </div>
            <div className="space-y-4">
              {availableDubEngines.length > 0 && (
                <div>
                  <label className="brutal-label" htmlFor="dub-engine">Voice engine</label>
                  <select id="dub-engine"
                    value={selectedDubEngine?.id || ""}
                    onChange={(e)=>{
                      const nextId = e.target.value;
                      setDubEngine(nextId);
                      const nextEngine = availableDubEngines.find(x => x.id === nextId);
                      const nextLangs = nextEngine?.languages || [];
                      if (nextLangs.length && !nextLangs.some(l => l.code === dubLang)) {
                        setDubLang(nextLangs[0]?.code || "");
                      }
                      setDubVoice("");
                    }}
                    className="brutal-input w-full"
                    data-testid="dub-engine-select"
                  >
                    {availableDubEngines.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="brutal-label" htmlFor="target-language">Target language</label>
                <select id="target-language"
                  value={dubLang}
                  onChange={(e)=>setDubLang(e.target.value)}
                  className="brutal-input w-full"
                  data-testid="dub-language-select"
                >
                  {dubDialogLangs
                    .filter(l => l.code !== proj.language)
                    .filter(l => !(proj.dubs || []).some(d => d.lang === l.code))
                    .map(l => (
                      <option key={l.code} value={l.code}>{l.label} ({l.code})</option>
                    ))}
                </select>
                {/* A language missing from this list is usually the source
                    language or one already dubbed, and silence about that
                    reads as a bug -- it was reported as one. */}
                {(() => {
                  const own = dubDialogLangs.find(l => l.code === proj.language);
                  const done = dubDialogLangs
                    .filter(l => (proj.dubs || []).some(d => d.lang === l.code))
                    .map(l => l.label);
                  const parts = [];
                  if (own) parts.push(`${own.label} is this video's own language`);
                  if (done.length) parts.push(`already dubbed: ${done.join(", ")}`);
                  return parts.length ? (
                    <p className="text-xs text-ink-muted mt-1" data-testid="dub-language-note">
                      Not listed — {parts.join("; ")}.
                    </p>
                  ) : null;
                })()}
              </div>
              <div>
                <label className="brutal-label" htmlFor="voice">Voice</label>
                <select id="voice"
                  value={dubVoice}
                  onChange={(e)=>setDubVoice(e.target.value)}
                  className="brutal-input w-full"
                  data-testid="dub-voice-select"
                >
                  <option value="">Default voice</option>
                  {dubDialogVoices.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <div className="text-xs text-ink-muted mt-1 uppercase tracking-widest">
                  {!dubEngineVoicesFallback && selectedDubEngine?.id === "pravaani"
                    ? "Pravaani — the platform's own TTS."
                    : "Sarvam Bulbul stock voices — natural-sounding across Indic languages."}
                </div>
                {/* The dub inherits the project's cloned voice automatically
                    when the engines match, which overrides this dropdown. Not
                    saying so leaves the user looking for a control that does
                    not exist. */}
                {proj.cloned_voice_id && proj.cloned_voice_provider === dubCreateProvider && (
                  <p className="text-xs mt-2" data-testid="dub-clone-note">
                    This dub will use your cloned voice
                    {proj.cloned_voice_name ? ` "${proj.cloned_voice_name}"` : ""} — the
                    selection above is ignored. Change it under the Video tab.
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={()=>setShowDubModal(false)} className="brutal-btn brutal-btn-secondary" data-testid="btn-cancel-dub">Cancel</button>
              <button
                disabled={creatingDub}
                onClick={async()=>{
                  setCreatingDub(true);
                  try {
                    await api.post(`/projects/${id}/dubs`, { lang: dubLang, voice: dubVoice, provider: dubCreateProvider });
                    toast.success("Dub queued — this can take a minute or two");
                    setShowDubModal(false);
                    load();
                  } catch(e){
                    toast.error(errMsg(e, "Failed to queue dub"));
                  } finally {
                    setCreatingDub(false);
                  }
                }}
                className="brutal-btn"
                data-testid="btn-create-dub"
              >
                {creatingDub ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Create dub
              </button>
            </div>
          </div>
        </div>
      )}

      {featureEnabled("social_publish") && showPublish && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="publish-modal" onClick={(e)=>{ if(e.target===e.currentTarget) setShowPublish(false); }}>
          <div className="brutal-card bg-white w-full max-w-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="marquee-tag mb-1">// PUBLISH TO SOCIAL · CREATOR+</div>
                <div className="text-xs uppercase tracking-widest text-ink-muted">Post this video straight to your connected accounts.</div>
              </div>
              <button onClick={()=>setShowPublish(false)} className="brutal-btn brutal-btn-ghost" data-testid="btn-close-publish" aria-label="Close dialog"><X size={14}/></button>
            </div>
            {socialStatus && !socialStatus.configured && (
              <div className="brutal-border p-3 bg-state-danger-surface text-xs uppercase tracking-widest mb-4" data-testid="publish-not-configured">
                Social publishing isn't configured on this server yet.
              </div>
            )}
            <div className="mb-4">
              {/* Multi-select toggles, not a labelled control — so this is a
                  named group, and each button carries aria-pressed. Selection
                  was previously conveyed by background colour alone. */}
              <span className="brutal-label" id="publish-platforms-label">Platforms</span>
              <div className="flex flex-wrap gap-2" role="group" aria-labelledby="publish-platforms-label">
                {SOCIAL_PLATFORMS.map(pf => (
                  <button
                    key={pf.id}
                    type="button"
                    onClick={()=>togglePlatform(pf.id)}
                    aria-pressed={publishPlatforms.includes(pf.id)}
                    className={`brutal-border px-3 py-1.5 text-xs uppercase tracking-widest ${publishPlatforms.includes(pf.id) ? "bg-surface-inverse text-white" : "hover:bg-surface"}`}
                    data-testid={`publish-platform-${pf.id}`}
                  >
                    {publishPlatforms.includes(pf.id) && <span aria-hidden="true">✓ </span>}
                    {pf.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <label className="brutal-label" htmlFor="publish-caption">Caption</label>
              <textarea id="publish-caption" className="brutal-input min-h-[80px]" value={publishCaption} onChange={(e)=>setPublishCaption(e.target.value)} data-testid="publish-caption"/>
            </div>
            <div className="flex items-center justify-between gap-2">
              <button onClick={connectSocial} className="brutal-btn brutal-btn-secondary" data-testid="btn-connect-social">
                {socialStatus?.profile ? "Manage accounts" : "Connect accounts"}
              </button>
              <div className="flex gap-2">
                <button onClick={()=>setShowPublish(false)} className="brutal-btn brutal-btn-secondary">Cancel</button>
                <button onClick={publishNow} disabled={publishing} className="brutal-btn" data-testid="btn-do-publish">
                  {publishing ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Publish
                </button>
              </div>
            </div>
            {(proj.publish_results?.length > 0) && (
              <div className="mt-4 pt-4 border-t brutal-border border-l-0 border-r-0 border-b-0">
                <div className="marquee-tag mb-2">// LAST PUBLISH</div>
                <div className="space-y-1">
                  {proj.publish_results.map((r,i)=>(
                    <div key={i} className="flex items-center justify-between text-2xs uppercase tracking-widest" data-testid={`publish-result-${r.platform}`}>
                      <span>{r.platform}</span>
                      <span className={r.status==="error" ? "text-ink-accent" : ""}>{r.status}{r.url ? " · posted" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showRegenConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
             role="dialog" aria-modal="true" aria-labelledby="regen-title" data-testid="regen-confirm"
             onKeyDown={trapRegenTab}>
          <div className="brutal-card bg-white max-w-lg w-full">
            <div id="regen-title" className="marquee-tag mb-3">// REGENERATE</div>
            {Object.keys(proj?.settings_diff || {}).length > 0 ? (
              <>
                <p className="text-sm mb-2 font-bold">Changed since the last render:</p>
                <ul className="text-sm mb-4 space-y-1" data-testid="regen-change-list">
                  {Object.entries(proj.settings_diff).map(([field, { was, now }]) => (
                    <li key={field}>
                      <span className="font-bold">{humanize(field)}</span>: {String(was ?? "none")} → {String(now ?? "none")}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm mb-4" data-testid="regen-no-changes">
                No settings have changed since the last render. Re-rendering will pick up any
                screenshot or annotation edits.
              </p>
            )}
            {isOwnerRole ? (
              quotaUsedValid && (
                <p className="text-xs uppercase tracking-widest text-ink-muted mb-4" data-testid="regen-quota">
                  {quotaUnlimited
                    ? "Unlimited renders on your plan"
                    : `Uses 1 of your ${quotaRemaining} remaining renders this month`}
                </p>
              )
            ) : (
              <p className="text-xs uppercase tracking-widest text-ink-muted mb-4" data-testid="regen-quota-editor">
                Uses 1 render from the project owner's plan.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button ref={regenCancelRef} className="brutal-btn brutal-btn-secondary" onClick={() => setShowRegenConfirm(false)}
                      data-testid="regen-cancel">Cancel</button>
              <button ref={regenConfirmRef} className="brutal-btn" onClick={doRegenerate} autoFocus
                      disabled={regenerating} data-testid="regen-confirm-btn">Regenerate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
